import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, UserRound, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { accountingApi, type EmployeeRecord } from '@/lib/api/accounting';
import { normalizeFaSearchText } from '@/lib/textNormalize';

function employeeLabel(e: EmployeeRecord): string {
  return e.full_name?.trim() || `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || `کارمند #${e.id}`;
}

const QuickEmployeeSearch: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<EmployeeRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const trimmed = q.trim();

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (trimmed.length < 2) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    const timer = window.setTimeout(() => {
      accountingApi
        .employees({ search: trimmed })
        .then((list) => {
          if (requestId !== requestIdRef.current) return;
          setResults(list.slice(0, 12));
          setActiveIndex(0);
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          setResults([]);
          setError(err instanceof Error ? err.message : 'خطا در جستجو');
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    }, 280);

    return () => window.clearTimeout(timer);
  }, [open, trimmed]);

  const goTo = (employee: EmployeeRecord) => {
    setOpen(false);
    setQ('');
    setResults([]);
    navigate(`/employees/${employee.id}#employee-advances`);
  };

  const hint = useMemo(() => {
    if (trimmed.length === 0) return 'نام، نام خانوادگی یا کد ملی را بنویسید';
    if (trimmed.length < 2) return 'حداقل ۲ حرف وارد کنید';
    if (loading) return 'در حال جستجو...';
    if (error) return error;
    if (results.length === 0) return 'نتیجه‌ای پیدا نشد';
    return null;
  }, [trimmed, loading, error, results.length]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setQ('');
          setResults([]);
          setError(null);
          setActiveIndex(0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700 shadow-sm transition hover:bg-sky-100 active:bg-sky-200"
          aria-label="جستجوی سریع کارمند"
          title="جستجوی سریع کارمند (Ctrl+K)"
        >
          <Search size={17} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={12}
        className="z-[80] w-[min(22rem,calc(100vw-1.5rem))] p-0"
        dir="rtl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-slate-100 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActiveIndex((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter' && results[activeIndex]) {
                  e.preventDefault();
                  goTo(results[activeIndex]);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              placeholder="نام یا کد ملی..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pe-3 ps-10 text-sm outline-none ring-sky-500 focus:ring-2"
              dir="rtl"
              autoComplete="off"
            />
            {q && (
              <button
                type="button"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                onClick={() => {
                  setQ('');
                  setResults([]);
                  inputRef.current?.focus();
                }}
                aria-label="پاک کردن"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">جستجو با نام یا کد ملی · میانبر Ctrl+K</p>
        </div>

        <div className="max-h-72 overflow-y-auto p-1.5">
          {loading && (
            <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" />
              در حال جستجو...
            </div>
          )}

          {!loading && hint && results.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-slate-400">{hint}</div>
          )}

          {!loading &&
            results.map((emp, idx) => {
              const name = employeeLabel(emp);
              const needle = normalizeFaSearchText(trimmed);
              const matchCode =
                needle && normalizeFaSearchText(emp.national_code ?? '').includes(needle);
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => goTo(emp)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-right transition ${
                    idx === activeIndex ? 'bg-sky-50 text-sky-900' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                    <UserRound size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800">{name}</div>
                    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      {emp.national_code && (
                        <span className={matchCode ? 'font-medium text-sky-700' : ''} dir="ltr">
                          کد ملی: {emp.national_code}
                        </span>
                      )}
                      {emp.employee_code && <span>کد پرسنلی: {emp.employee_code}</span>}
                      {emp.mobile && <span dir="ltr">{emp.mobile}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default QuickEmployeeSearch;
